import React, { FC, useCallback, useEffect, useState } from 'react';
import { useMutation } from 'react-relay';
import { stringFormat, T } from 'src/shared/helpers';
import styled from 'styled-components';
import { Checkbox } from '../../../widgets/checkbox';
import { InputDialog, QuestionDialog, useDialogManager } from '../../../widgets/dialog';
import { useDropdown } from '../../../widgets/hooks/useDropdown';
import { showErrorNotification, showSuccessNotification } from '../../../widgets/notificationToaster';
import { ZTooltip } from '../../../widgets/ztooltip';
import { MoveToCategoryDialog } from '../components/results/moveToCategoryDialog';
import { ActionButton, DropdownDivider, DropdownMenuItem, ExpandButton } from '../components/shared/sharedStyles';
import { downloadFile } from '../utils/downloadHelpers';
import { assetLibraryQueriesDeleteAIAssetLibraryAssetMutation } from './__generated__/assetLibraryQueriesDeleteAIAssetLibraryAssetMutation.graphql';
import { assetLibraryQueriesUpdateAIAssetLibraryAssetMutation } from './__generated__/assetLibraryQueriesUpdateAIAssetLibraryAssetMutation.graphql';
import { assetsGrid_query$data } from './__generated__/assetsGrid_query.graphql';
import { AssetInfoDialog } from './assetInfoDialog';
import { deleteAIAssetLibraryAssetMutation, updateAIAssetLibraryAssetMutation } from './assetLibraryQueries';

type AssetNode = NonNullable<NonNullable<assetsGrid_query$data['aiAssetLibraryAssets']>['edges']>[number]['node'];

export interface LocalModel3DItem {
    id: string;
    label: string;
    toolId: string;
    thumbnailUrl?: string;
    modelUrl?: string;
    prompt?: string;
    createdAt: number;
}

// Icons
import ArrowRightIcon from 'src/shared/icons/arrow-right-solid.svg';
import CubeIcon from 'src/shared/icons/cube-solid.svg';
import DownloadIcon from 'src/shared/icons/download-solid.svg';
import EditIcon from 'src/shared/icons/edit-solid.svg';
import EllipsisVIcon from 'src/shared/icons/ellipsis-v-solid.svg';
import ExpandIcon from 'src/shared/icons/expand-solid.svg';
import FolderIcon from 'src/shared/icons/folder-solid.svg';
import InfoCircleIcon from 'src/shared/icons/info-circle-solid.svg';
import PlayIcon from 'src/shared/icons/play-solid.svg';
import TrashIcon from 'src/shared/icons/trash-alt-solid.svg';
import VideoIcon from 'src/shared/icons/video-solid.svg';

const GlbViewer = React.lazy(() => import('../components/shared/glbViewer'));

function isModelAsset(asset: { assetType: string }): boolean {
    return asset.assetType === '3d-model';
}

const Card = styled.div`
    position: relative;
    border-radius: 8px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.backgroundColorBase};
    transition: all 0.2s;
    min-width: 0;
    overflow: hidden;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const AssetPreview = styled.div`
    aspect-ratio: 1;
    position: relative;
    background: #f5f5f5;
`;

const AssetImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const AssetVideo = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const VideoOverlay = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 4px;

    svg {
        width: 12px;
        height: 12px;
        fill: white;
    }
`;

const CheckboxWrapper = styled.div`
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 2;
`;

const MenuButtonWrapper = styled.div`
    position: absolute;
    bottom: 8px;
    left: 8px;
`;

const AssetInfo = styled.div`
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

const AssetName = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: ${(props) => props.theme.app.textColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AssetMeta = styled.div`
    font-size: 11px;
    color: #78909c;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const CategoryBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    background: ${(props) => props.theme.app.primary_light};
    color: ${(props) => props.theme.app.primary};
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;

    svg {
        width: 10px;
        height: 10px;
        fill: currentColor;
    }
`;

const PlaceholderWrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0ebff;
`;

const Placeholder3D: FC = () => (
    <PlaceholderWrapper>
        <svg width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='#a855f7' strokeWidth='1.5'>
            <path d='M12 3L2 7.5 12 12 22 7.5 12 3z' />
            <path d='M2 12l10 4.5L22 12' />
            <path d='M2 16.5l10 4.5 10-4.5' />
        </svg>
    </PlaceholderWrapper>
);

interface AssetItemProps {
    asset: AssetNode;
    onAssetChange: () => void;
    /** Callback when user wants to use this asset as input for a tool */
    onUseAsInput?: (imageUrl: string) => void;
    /** Whether this asset is currently selected */
    isSelected?: boolean;
    /** Toggle selection callback */
    onToggleSelect?: (id: string) => void;
    /** Whether this item's menu is currently open (controlled by parent) */
    isMenuOpen?: boolean;
    /** Notify parent that this item's menu should open */
    onMenuOpen?: (id: string) => void;
    /** Notify parent that the menu should close */
    onMenuClose?: () => void;
    /** Callback to open the lightbox for this asset */
    onExpand?: () => void;
}

export const AssetItem: FC<AssetItemProps> = ({
    asset,
    onAssetChange,
    onUseAsInput,
    isSelected,
    onToggleSelect,
    isMenuOpen,
    onMenuOpen,
    onMenuClose,
    onExpand
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [setOpen, setClose, isOpen, Dropdown] = useDropdown();
    const [thumbnailBroken, setThumbnailBroken] = useState(false);

    useEffect(() => {
        setThumbnailBroken(false);
    }, [asset.thumbnailUrl, asset.fileUrl]);

    // Sync internal dropdown state with parent-controlled isMenuOpen
    useEffect(() => {
        if (!isMenuOpen && isOpen) {
            setClose();
        }
    }, [isMenuOpen, isOpen, setClose]);

    const [updateAsset] = useMutation<assetLibraryQueriesUpdateAIAssetLibraryAssetMutation>(updateAIAssetLibraryAssetMutation);
    const [deleteAsset] = useMutation<assetLibraryQueriesDeleteAIAssetLibraryAssetMutation>(deleteAIAssetLibraryAssetMutation);

    const handleDownload = useCallback(async () => {
        const extension = isModelAsset(asset) ? 'glb' : asset.assetType === 'video' ? 'mp4' : 'png';
        try {
            await downloadFile(asset.fileUrl, `ai-asset-${asset.name}.${extension}`);
        } catch (error) {
            console.error('Failed to download file:', error);
        }
    }, [asset]);

    const handleRename = useCallback(
        (newName: string) => {
            if (!newName.trim()) {
                showErrorNotification({ message: T._('Type a valid name', 'Admin') });
                return;
            }

            updateAsset({
                variables: {
                    id: asset.id,
                    name: newName.trim(),
                    categoryID: asset.categoryID ? parseInt(asset.categoryID.toString()) : null
                },
                onCompleted: () => {
                    showSuccessNotification({ message: T._('Asset renamed.', 'Admin') });
                    closeDialog('rename-asset');
                    onAssetChange();
                },
                onError: (error) => {
                    console.error('Failed to rename asset:', error);
                    showErrorNotification({ message: T._('Failed to rename asset', 'Admin') });
                }
            });
        },
        [asset, updateAsset, onAssetChange, closeDialog]
    );

    const handleMove = useCallback(
        (categoryId: number | null) => {
            updateAsset({
                variables: {
                    id: asset.id,
                    name: asset.name,
                    categoryID: categoryId
                },
                onCompleted: () => {
                    showSuccessNotification({ message: T._('Asset moved.', 'Admin') });
                    closeDialog('move-asset');
                    onAssetChange();
                },
                onError: (error) => {
                    console.error('Failed to move asset:', error);
                    showErrorNotification({ message: T._('Failed to move asset', 'Admin') });
                },
                updater: (store) => {
                    store.invalidateStore();
                }
            });
        },
        [asset, updateAsset, onAssetChange, closeDialog]
    );

    const handleDelete = useCallback(() => {
        deleteAsset({
            variables: {
                id: asset.id
            },
            onCompleted: () => {
                showSuccessNotification({ message: T._('Asset deleted.', 'Admin') });
                closeDialog('delete-asset');
                onAssetChange();
            },
            onError: (error) => {
                console.error('Failed to delete asset:', error);
                showErrorNotification({ message: T._('Failed to delete asset', 'Admin') });
            },
            updater: (store) => {
                store.invalidateStore();
            }
        });
    }, [asset, deleteAsset, onAssetChange, closeDialog]);

    const openRenameDialog = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            showDialog('rename-asset', <InputDialog label={T._('Asset Name', 'Admin')} defaultValue={asset.name} onConfirm={handleRename} />);
        },
        [asset.name, showDialog, handleRename]
    );

    const openMoveDialog = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            showDialog(
                'move-asset',
                <MoveToCategoryDialog currentCategoryId={asset.categoryID} onMove={handleMove} onClose={() => closeDialog('move-asset')} />
            );
        },
        [asset.categoryID, showDialog, handleMove, closeDialog]
    );

    const openDeleteDialog = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            showDialog(
                'delete-asset',
                <QuestionDialog
                    message={stringFormat(T._('Are you sure you want to delete "{0}"? This action cannot be undone.', 'Admin'), asset.name)}
                    onYesClick={handleDelete}
                />
            );
        },
        [asset.name, showDialog, handleDelete]
    );

    const openInfoDialog = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            showDialog('asset-info', <AssetInfoDialog metadata={asset.metadata} assetName={asset.name} onClose={() => closeDialog('asset-info')} />);
        },
        [asset.metadata, asset.name, showDialog, closeDialog]
    );

    const handleUseAsInput = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (onUseAsInput) {
                onUseAsInput(asset.fileUrl);
            }
        },
        [asset.fileUrl, onUseAsInput]
    );

    const handleExpand = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onExpand?.();
        },
        [onExpand]
    );

    const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    const handleCheckboxChange = useCallback(() => {
        if (onToggleSelect) {
            onToggleSelect(asset.id);
        }
    }, [asset.id, onToggleSelect]);

    const handleMenuToggle = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (isOpen) {
                setClose();
                onMenuClose?.();
            } else {
                onMenuOpen?.(asset.id);
                setOpen(e.currentTarget as HTMLElement, 'top', 'left');
            }
        },
        [isOpen, setOpen, setClose, onMenuOpen, onMenuClose, asset.id]
    );

    // Renders the preview tile. Falls back to the 3D placeholder when an image-typed asset has no
    // usable thumbnail/fileUrl, or when the loaded image fails — covers freshly-saved 3D models
    // before a backend thumbnail is available.
    const renderPreview = () => {

        const src = asset.thumbnailUrl || asset.fileUrl;

        if (asset.assetType === 'video') {
            return (
                <>
                    <AssetVideo src={asset.fileUrl} muted loop />
                    <VideoOverlay>
                        <VideoIcon />
                        {asset.durationSeconds ? `${Math.round(asset.durationSeconds)}s` : 'Video'}
                    </VideoOverlay>
                </>
            );
        }
        if (isModelAsset(asset)) {
            return (
                <>
                    {/* <Suspense fallback={null}>
                        <GlbViewer url={asset.fileUrl} />
                    </Suspense> */}
                    <Placeholder3D />       
                    <VideoOverlay>
                        <CubeIcon />
                        {'3D'}
                    </VideoOverlay>
                </>
            );
        }

        if (src && !thumbnailBroken) {
            return <AssetImage src={src} alt={asset.name} onError={() => setThumbnailBroken(true)} />;
        }
        return <Placeholder3D />
    };

    return (
        <Card>
            <AssetPreview>
                {renderPreview()}

                {onToggleSelect && (
                    <CheckboxWrapper onClick={handleCheckboxClick}>
                        <Checkbox checked={!!isSelected} onChange={handleCheckboxChange} />
                    </CheckboxWrapper>
                )}

                <MenuButtonWrapper>
                    <ActionButton onClick={handleMenuToggle}>
                        <EllipsisVIcon />
                    </ActionButton>
                </MenuButtonWrapper>

                {isOpen && (
                    <Dropdown>
                        {onUseAsInput && asset.assetType !== 'video' && (
                            <>
                                <DropdownMenuItem onClick={handleUseAsInput}>
                                    <ArrowRightIcon />
                                    {T._('Use as Input', 'Admin')}
                                </DropdownMenuItem>
                                <DropdownDivider />
                            </>
                        )}
                        <DropdownMenuItem onClick={handleDownload}>
                            <DownloadIcon />
                            {T._('Download', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={openInfoDialog}>
                            <InfoCircleIcon />
                            {T._('Show Info', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={openRenameDialog}>
                            <EditIcon />
                            {T._('Rename', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={openMoveDialog}>
                            <FolderIcon />
                            {T._('Move to Category', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownDivider />
                        <DropdownMenuItem $danger onClick={openDeleteDialog}>
                            <TrashIcon />
                            {T._('Delete', 'Admin')}
                        </DropdownMenuItem>
                    </Dropdown>
                )}

                <ZTooltip
                    position='bottom'
                    title={asset.assetType === 'video' ? T._('Play Video', 'Admin') : T._('View Full Size', 'Admin')}
                    enterDelay={0}
                    exitDelay={0}
                    interactive={false}
                >
                    <ExpandButton onClick={handleExpand}>{asset.assetType === 'video' ? <PlayIcon /> : <ExpandIcon />}</ExpandButton>
                </ZTooltip>
            </AssetPreview>

            <ZTooltip position='bottom' title={asset.name} enterDelay={0} exitDelay={0} interactive={false}>
                <AssetInfo>
                    <AssetName>{asset.name}</AssetName>
                    <AssetMeta>
                        {asset.width && asset.height && (
                            <span>
                                {asset.width} × {asset.height}
                            </span>
                        )}
                        {asset.categoryName && (
                            <CategoryBadge>
                                <FolderIcon />
                                {asset.categoryName}
                            </CategoryBadge>
                        )}
                    </AssetMeta>
                </AssetInfo>
            </ZTooltip>
        </Card>
    );
};
